import { useMemo } from 'react';
import { Banknote, Clock, FileText, Wallet } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import DataTable from '@/components/data-table/data-table';
import { StatCard } from '@/components/stat-card';

import { formatCurrency, formatDateTime, formatNumber } from '@/utils/format';

import type {
  Transaction,
  TransactionStatus,
  TransactionType,
} from '@/features/finances/finances.types';
import type { ColumnDef } from '@tanstack/react-table';

import { useFinancesPage } from './use-finances-page';

const TYPE_LABEL: Record<TransactionType, string> = {
  payout: 'Liquidación',
  charge: 'Cobro',
  refund: 'Reembolso',
};

const STATUS_LABEL: Record<TransactionStatus, string> = {
  pending: 'Pendiente',
  completed: 'Completada',
  failed: 'Fallida',
};

const STATUS_BADGE_CLASS: Record<TransactionStatus, string> = {
  pending: 'badge-warning',
  completed: 'badge-success',
  failed: 'badge-error',
};

/**
 * Finances dashboard view.
 * Shows the headline financial KPIs and the recent transactions feed.
 */
export function FinancesPage() {
  const { summary, transactions, isLoading, isError } = useFinancesPage();

  const columns = useMemo<ColumnDef<Transaction>[]>(
    () => [
      {
        accessorKey: 'concept',
        header: 'Concepto',
        cell: (info) => <span className="font-semibold">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'counterparty',
        header: 'Contraparte',
      },
      {
        accessorKey: 'type',
        header: 'Tipo',
        size: 130,
        cell: (info) => TYPE_LABEL[info.getValue() as TransactionType],
      },
      {
        id: 'amount',
        header: 'Monto',
        size: 140,
        meta: { align: 'right' },
        cell: (info) => formatCurrency(info.row.original.amount, info.row.original.currency),
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        size: 130,
        cell: (info) => {
          const status = info.getValue() as TransactionStatus;
          return (
            <span className={twMerge('badge', STATUS_BADGE_CLASS[status])}>
              {STATUS_LABEL[status]}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Fecha',
        size: 150,
        cell: (info) => formatDateTime(info.getValue() as string),
      },
    ],
    [],
  );

  if (isError) {
    return (
      <div className="flex justify-center p-8">
        <div className="alert alert-error max-w-lg">
          <span>No se pudo cargar la información financiera. Intenta de nuevo.</span>
        </div>
      </div>
    );
  }

  const currency = summary?.currency ?? 'MXN';

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold">Finanzas</h1>
        <p className="text-base-content/70">
          Resumen de ingresos, liquidaciones a puntos y conciliaciones.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Ingresos del mes"
          value={formatCurrency(summary?.monthly_revenue ?? 0, currency)}
          Icon={Banknote}
          tone="success"
        />
        <StatCard
          label="Liquidaciones pendientes"
          value={formatCurrency(summary?.pending_payouts ?? 0, currency)}
          Icon={Clock}
          tone="warning"
        />
        <StatCard
          label="Pagado a puntos"
          value={formatCurrency(summary?.paid_to_places ?? 0, currency)}
          Icon={Wallet}
          tone="primary"
        />
        <StatCard
          label="Conciliaciones pendientes"
          value={formatNumber(summary?.pending_reconciliations ?? 0)}
          Icon={FileText}
          tone="info"
        />
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-semibold">Transacciones recientes</h2>
        <DataTable columns={columns} data={transactions} isLoading={isLoading} />
      </section>
    </div>
  );
}
